import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Divider,
  FileInput,
  Group,
  List,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconFileImport, IconSearch, IconUpload } from '@tabler/icons-react';
import type {
  GngEtsngLinkRow,
  LinkCandidateDto,
  LinkFrom,
  ReferenceBookMetaDto,
} from '@expeditor/shared';
import { referenceDataApi } from '../../shared/api/reference-data';
import { colIndex, parseGrid } from '../../shared/lib/csv';

const CHUNK = 400;

/** Разбор файла переходника в строки (по колонкам официального файла). */
function parseLink(text: string): GngEtsngLinkRow[] {
  const { rows, header } = parseGrid(text);
  const iGng = colIndex(header, ['kod_gng', 'gng', 'код_гнг'], 0);
  const iGngName = colIndex(header, ['naimenovanie_gng'], 1);
  const iRelGE = colIndex(header, ['otnesenie_gng_k_etsng'], 2);
  const iEtsng = colIndex(header, ['kod_etsng', 'etsng', 'код_етснг'], 3);
  const iEtsngName = colIndex(header, ['naimenovanie_etsng'], 4);
  const iRelEG = colIndex(header, ['otnesenie_etsng_k_gng'], 5);
  const iClass = colIndex(header, ['tarifny_klass_etsng', 'tarifny_klass', 'klass'], 7);
  const iDir = colIndex(header, ['napravleniya'], 8);
  const iRed = colIndex(header, ['kod_gng_v_red_2026'], 9);
  const iRzhd = colIndex(header, ['kod_etsng_v_sprav_rzhd'], 10);
  const out: GngEtsngLinkRow[] = [];
  for (const c of rows) {
    const gngCode = (c[iGng] ?? '').trim();
    const etsngCode = (c[iEtsng] ?? '').trim();
    if (!gngCode || !etsngCode) continue;
    out.push({
      gngCode,
      etsngCode,
      gngName: (c[iGngName] ?? '').trim().slice(0, 1000) || null,
      etsngName: (c[iEtsngName] ?? '').trim().slice(0, 1000) || null,
      tariffClass: (c[iClass] ?? '').trim() || null,
      relGngEtsng: (c[iRelGE] ?? '').trim() || null,
      relEtsngGng: (c[iRelEG] ?? '').trim() || null,
      directions: (c[iDir] ?? '').trim() || null,
      gngInRed2026: (c[iRed] ?? '').trim() || null,
      etsngInRzhd: (c[iRzhd] ?? '').trim() === '1',
    });
  }
  return out;
}

/** Убирает дубли по паре кодов. */
function dedupe(rows: GngEtsngLinkRow[]): GngEtsngLinkRow[] {
  const map = new Map<string, GngEtsngLinkRow>();
  for (const r of rows) map.set(`${r.gngCode}|${r.etsngCode}`, r);
  return [...map.values()];
}

/** Админ: переходник ГНГ↔ЕТСНГ — состояние, импорт, проверка связи. */
export function LinkBookPage() {
  const [meta, setMeta] = useState<ReferenceBookMetaDto | null>(null);
  const [text, setText] = useState('');
  const [source, setSource] = useState('');
  const [parsed, setParsed] = useState<GngEtsngLinkRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // Проверка связи.
  const [from, setFrom] = useState<LinkFrom>('gng');
  const [code, setCode] = useState('');
  const [candidates, setCandidates] = useState<LinkCandidateDto[] | null>(null);

  useEffect(() => {
    referenceDataApi
      .linkMeta()
      .then(setMeta)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить сведения'));
  }, []);

  const onText = (value: string) => {
    setText(value);
    setParsed(dedupe(parseLink(value)));
    setOkMsg(null);
  };

  const onFile = async (file: File | null) => {
    if (!file) return;
    onText(await file.text());
  };

  const doImport = async () => {
    if (parsed.length === 0) return;
    if (!window.confirm(`Загрузить ${parsed.length} пар? Текущий переходник будет заменён.`)) return;
    setBusy(true);
    setError(null);
    setOkMsg(null);
    setProgress({ done: 0, total: parsed.length });
    try {
      let last: ReferenceBookMetaDto | null = null;
      for (let i = 0; i < parsed.length; i += CHUNK) {
        last = await referenceDataApi.importLink({
          rows: parsed.slice(i, i + CHUNK),
          replace: i === 0,
          source: i === 0 ? source.trim() || undefined : undefined,
        });
        setProgress({ done: Math.min(i + CHUNK, parsed.length), total: parsed.length });
      }
      if (last) setMeta(last);
      setOkMsg(`Загружено пар: ${last?.rowCount ?? parsed.length}.`);
      setText('');
      setParsed([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка импорта');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const doCheck = async () => {
    const c = code.trim();
    if (!c) return;
    try {
      setCandidates(await referenceDataApi.link(from, c));
    } catch {
      setCandidates([]);
    }
  };

  const preview = parsed.slice(0, 5);

  return (
    <Stack>
      <Anchor component={Link} to="/manager/reference-data" size="sm">
        <Group gap={4}>
          <IconArrowLeft size={14} /> Все справочники
        </Group>
      </Anchor>
      <Title order={2}>Переходник ГНГ ↔ ЕТСНГ</Title>
      <Text c="dimmed" size="sm">
        Официальные перекодировочные таблицы Дирекции Совета по ж/д транспорту. В форме поручения по
        одному коду подставляется встречный (при единственном варианте) или предлагается выбор.
        Связь неоднозначна в обе стороны.
      </Text>

      {error && <Alert color="red">{error}</Alert>}
      {okMsg && <Alert color="teal">{okMsg}</Alert>}

      <Card withBorder padding="md">
        <Group justify="space-between">
          <Stack gap={2}>
            <Text fw={500}>Текущее состояние</Text>
            <Text size="sm" c="dimmed">
              Источник: {meta?.source ?? '—'}
            </Text>
            <Text size="sm" c="dimmed">
              Обновлён: {meta?.updatedAt ? new Date(meta.updatedAt).toLocaleString('ru-RU') : '—'}
            </Text>
          </Stack>
          <Badge size="lg" variant="light">
            {meta?.rowCount ?? 0} пар
          </Badge>
        </Group>
      </Card>

      <Divider label="Проверка связи" labelPosition="left" />
      <Group align="flex-end">
        <SegmentedControl
          value={from}
          onChange={(v) => setFrom(v as LinkFrom)}
          data={[
            { label: 'ГНГ → ЕТСНГ', value: 'gng' },
            { label: 'ЕТСНГ → ГНГ', value: 'etsng' },
          ]}
        />
        <TextInput
          placeholder={from === 'gng' ? 'Код ГНГ' : 'Код ЕТСНГ'}
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && doCheck()}
        />
        <Button variant="default" leftSection={<IconSearch size={16} />} onClick={doCheck}>
          Показать
        </Button>
      </Group>
      {candidates && (
        <Card withBorder padding="sm">
          {candidates.length === 0 ? (
            <Text size="sm" c="dimmed">
              Соответствий не найдено.
            </Text>
          ) : (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={120}>Код</Table.Th>
                  <Table.Th>Наименование</Table.Th>
                  <Table.Th w={200}>Доп.</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {candidates.map((x) => (
                  <Table.Tr key={x.code}>
                    <Table.Td>{x.code}</Table.Td>
                    <Table.Td>{x.name}</Table.Td>
                    <Table.Td>
                      {x.detail ?? ''}
                      {!x.production ? ' (нет в текущих справочниках)' : ''}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      )}

      <Divider label="Импорт" labelPosition="left" />
      <Alert color="blue" variant="light">
        <Text size="sm">
          Файл CSV/TSV официального переходника. Колонки распознаются по заголовку
          (<Code>kod_gng, naimenovanie_gng, otnesenie_gng_k_etsng, kod_etsng, naimenovanie_etsng, …,
          tarifny_klass_etsng, napravleniya, kod_gng_v_red_2026, kod_etsng_v_sprav_rzhd</Code>) или
          по порядку.
        </Text>
        <List size="sm" mt={4}>
          <List.Item>Разделитель и строка-заголовок определяются автоматически.</List.Item>
          <List.Item>Загрузка полностью заменяет текущий переходник.</List.Item>
          <List.Item>Обновляйте при выходе новой редакции (публикуется с суффиксом ГГММ).</List.Item>
        </List>
      </Alert>

      <FileInput
        label="Файл переходника (.csv / .tsv)"
        placeholder="Выберите файл"
        accept=".csv,.tsv,.txt"
        leftSection={<IconFileImport size={16} />}
        value={null}
        onChange={onFile}
        disabled={busy}
      />
      <Textarea
        label="…или вставьте содержимое"
        autosize
        minRows={3}
        maxRows={8}
        value={text}
        onChange={(e) => onText(e.currentTarget.value)}
        disabled={busy}
      />
      <TextInput
        label="Источник / версия (метка)"
        placeholder="Например: Совет по ж/д транспорту, ред. 01.07.2025"
        value={source}
        onChange={(e) => setSource(e.currentTarget.value)}
        disabled={busy}
      />

      {parsed.length > 0 && (
        <Card withBorder padding="sm">
          <Text size="sm" mb={6}>
            Распознано пар: <b>{parsed.length}</b>. Предпросмотр первых {preview.length}:
          </Text>
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ГНГ</Table.Th>
                <Table.Th>ЕТСНГ</Table.Th>
                <Table.Th>Наименование ЕТСНГ</Table.Th>
                <Table.Th>Класс</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {preview.map((r) => (
                <Table.Tr key={`${r.gngCode}|${r.etsngCode}`}>
                  <Table.Td>{r.gngCode}</Table.Td>
                  <Table.Td>{r.etsngCode}</Table.Td>
                  <Table.Td>{r.etsngName}</Table.Td>
                  <Table.Td>{r.tariffClass}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {progress && (
        <Text size="xs" c="dimmed">
          Загружено {progress.done} из {progress.total}…
        </Text>
      )}

      <Group>
        <Button
          leftSection={<IconUpload size={16} />}
          onClick={doImport}
          loading={busy}
          disabled={parsed.length === 0}
        >
          Импортировать ({parsed.length})
        </Button>
      </Group>
    </Stack>
  );
}
